import { MoreVertical, Pencil, Download, Trash2, Copy, Move, Share2, Star, Film, Image as ImageIcon } from 'lucide-react';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { useItemListContext } from './ItemListProvider';
import { useItemActions } from '@/hooks/useItemActions';
import type { NormalizedItem } from './types';

interface ItemActionMenuProps {
  item: NormalizedItem;
}

export function ItemActionMenu({ item }: ItemActionMenuProps) {
  const { permissions, openModal, updateIsQuerying, onRefresh } = useItemListContext();
  const { downloadItem, isFavorite, toggleFavorite } = useItemActions({
    updateIsQuerying,
    onRefresh,
  });

  const isResource = item.itemtype === 'resource';
  const isVideo = item.type === 'video';
  const favorite = isFavorite(item.id);

  return (
    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="relative z-10 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
      <Dropdown
        trigger={
          <button className="rounded-lg p-1 hover:bg-secondary transition-colors" title="Actions">
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        }
        align="right"
      >
      {permissions.canEdit && (
        <DropdownItem onClick={() => openModal('rename', { item })}>
          <Pencil className="mr-2 h-4 w-4" /> Rename
        </DropdownItem>
      )}
      {isResource && (
        <DropdownItem onClick={() => downloadItem(item)}>
          <Download className="mr-2 h-4 w-4" /> Download
        </DropdownItem>
      )}
      <DropdownItem onClick={() => toggleFavorite(item)}>
        <Star className={`mr-2 h-4 w-4 ${favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
        {favorite ? 'Remove favorite' : 'Add to favorites'}
      </DropdownItem>
      {permissions.canMove && (
        <DropdownItem onClick={() => openModal('move', { items: [item] })}>
          <Move className="mr-2 h-4 w-4" /> Move
        </DropdownItem>
      )}
      {permissions.canCopy && isResource && (
        <DropdownItem onClick={() => openModal('copy', { items: [item] })}>
          <Copy className="mr-2 h-4 w-4" /> Copy
        </DropdownItem>
      )}
      {permissions.canShare && isResource && (
        <DropdownItem onClick={() => openModal('shareFile', { items: [item] })}>
          <Share2 className="mr-2 h-4 w-4" /> Share
        </DropdownItem>
      )}
      {!isResource && permissions.canEdit && (
        <DropdownItem onClick={() => openModal('coverImage', { item })}>
          <ImageIcon className="mr-2 h-4 w-4" /> Cover image
        </DropdownItem>
      )}
      {isVideo && permissions.canEdit && (
        <DropdownItem onClick={() => openModal('convert', { item })}>
          <Film className="mr-2 h-4 w-4" /> Convert to MP4
        </DropdownItem>
      )}
      {permissions.canDelete && (
        <DropdownItem destructive onClick={() => openModal('delete', { items: [item] })}>
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownItem>
      )}
    </Dropdown>
    </div>
  );
}
